import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/content';
import { Segmented } from '@/components/ui/kit';
import { formatCount } from '@/i18n/format';
import { useNamespaceCopy } from '@/i18n/ns';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import libraryFeedCopy from '@/i18n/ns/library-feed';
import libraryUploadCopy from '@/i18n/ns/library-upload';
import shareChipCopy from '@/i18n/ns/share-chip';
import { useRelativeTimeLabel } from '../artifact-panels/format';
import { WorkspaceIcon } from '../icons';
import { useIntlLocale } from '../intl-locale';
import { EmptyState, LoadingState } from '../primitives';
import type { CompanyFile } from '../types';
import type { LibraryFeedItem, LibraryFeedOpenTarget, LibraryGroup } from './api';
import { LibraryAddRow } from './library-add-row';
import { groupByDate, type LibraryDateGroup } from './library-dates';
import { LibraryOwnerMenu } from './library-owner-menu';
import { LibraryItemRow, LibraryUploadRow } from './library-rows';
import { ownPictureUrl, ownResultPictureUrl } from '../artifacts/api';
import { libraryUploads, useLibraryUploads, type LibraryUpload } from './upload-queue';
import { useLibraryDrop, useStrayDropGuard } from './use-library-drop';
import type { LibraryFeedState } from './use-library-feed';
import { useUploadAnnouncement, useUploadShortcut } from './use-library-upload-controls';
import './library-feed.css';
import { DeleteLibraryItem } from '../item-archive/delete-item';
import { LibraryArchive } from '../item-archive/archive';

const EMPTY_KIND = {
  documents: 'emptyDocuments',
  decks: 'emptyDecks',
  sheets: 'emptySheets',
  files: 'emptyFiles',
} as const;
const GROUP_TITLE = {
  today: 'groupToday',
  yesterday: 'groupYesterday',
  earlier: 'groupEarlier',
} as const;
/** How long a just-saved row keeps its arrival tint (the CSS animation's length). */
const ARRIVAL_MS = 1500;

/** A saved work's own picture (a deck's first slide, a Result's Overview) from the path the feed gave it; files have
 *  none. */
const feedPicture = ({ openTarget: target, thumbnail }: LibraryFeedItem) =>
  target.kind === 'artifact'
    ? ownPictureUrl(
        {
          artifactId: target.id,
          versionId: target.versionId,
          revisionId: target.revisionId,
          purpose: target.purpose,
          thumbnailRef: thumbnail.ref,
        },
        thumbnail.picture,
      )
    : target.kind === 'result'
      ? ownResultPictureUrl({ resultId: target.id, thumbnailRef: thumbnail.ref }, thumbnail.picture)
      : null;
const readable = (file: CompanyFile) => !file.processingStatus || file.processingStatus === 'ready';

export function LibraryFeedView({
  feed,
  onScreen,
  viewerName,
  onUseInChat,
  onOpen,
  onMake,
  onOpenApps,
}: {
  feed: LibraryFeedState;
  /** False while the Library is behind another view: no shortcut and no drop target. */
  onScreen: boolean;
  /** The owner line of a file the viewer has just uploaded. */
  viewerName: string | null;
  /** Present when there is a chat to bring a just-uploaded file into ("Use in chat"). */
  onUseInChat?: (fileId: string) => void;
  /** Opens exactly the row's own target, unchanged. */
  onOpen: (target: LibraryFeedOpenTarget) => void;
  onMake: () => void;
  /** "N Apps match in Apps →": the Apps place, searching for the same words. */
  onOpenApps: (query: string) => void;
}) {
  const panels = useNamespaceCopy(artifactPanelsCopy);
  const copy = useNamespaceCopy(libraryFeedCopy);
  const uploadCopy = useNamespaceCopy(libraryUploadCopy);
  const tags = useNamespaceCopy(shareChipCopy);
  const locale = useIntlLocale();
  const timeLabel = useRelativeTimeLabel();
  const anchor = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const uploads = useLibraryUploads();
  const add = useCallback((files: File[]) => libraryUploads.add(files), []);
  // Adding files is Files' own: its first row, a drop target over the page, and ⌘U. Every kind keeps a stray drop
  // from opening the file in place of Chat.
  const filesView = feed.group === 'files';
  useStrayDropGuard(onScreen);
  const dragging = useLibraryDrop(anchor, onScreen && filesView, add);
  useUploadShortcut(input, onScreen && filesView);
  const announcement = useUploadAnnouncement(uploads, dragging, uploadCopy, locale);

  // On a phone the kind track may scroll sideways in a long language: the chosen kind stays in view.
  const toolbar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const track = toolbar.current?.querySelector<HTMLElement>('.pc-segmented');
    const chosen = track?.querySelector<HTMLElement>('[aria-checked="true"]');
    if (!track || !chosen || track.scrollWidth <= track.clientWidth) return;
    const row = track.getBoundingClientRect();
    const kind = chosen.getBoundingClientRect();
    if (kind.left < row.left) track.scrollLeft -= row.left - kind.left + 8;
    else if (kind.right > row.right) track.scrollLeft += kind.right - row.right + 8;
  }, [feed.group]);
  const searching = feed.query.trim() !== '';
  const ready = feed.load === 'ready';
  const groupLabel: Record<LibraryGroup, string> = {
    all: panels.librarySegmentAll,
    documents: panels.libraryKindDocuments,
    decks: panels.libraryKindDecks,
    sheets: panels.libraryKindSpreadsheets,
    files: copy.kindFiles,
  };
  const shortLabel: Partial<Record<LibraryGroup, string>> = {
    documents: copy.kindDocsShort,
    sheets: copy.kindSheetsShort,
  };

  // A just-uploaded file keeps its place at the top of Today; the feed's own row for it is not listed twice.
  const savedFile = (upload: LibraryUpload) => (upload.status === 'done' ? upload.file : null);
  const savedIds = new Set(uploads.map(savedFile).flatMap((file) => (file ? [file.id] : [])));
  const feedFile = new Map(
    feed.items.filter((item) => item.kind === 'file').map((item) => [item.openTarget.id, item]),
  );
  const listed = feed.items.filter(
    (item) => !(item.kind === 'file' && savedIds.has(item.openTarget.id)),
  );
  // Whether the current filter takes a private file of the viewer's (search is the server's to say).
  const filterTakesFiles =
    (feed.group === 'all' || feed.group === 'files') && feed.segment !== 'shared' && !searching;

  const ownerLine = (item: LibraryFeedItem) => {
    const state =
      item.state === 'draft'
        ? copy.stateDraft
        : item.state === 'ready'
          ? copy.stateReady
          : item.state === 'changed-since-ready'
            ? copy.stateChanged
            : null;
    const stored =
      item.kind === 'file' && item.processingStatus === 'stored_only'
        ? uploadCopy.storedOnly
        : null;
    return [item.owner.label, state, stored].filter(Boolean).join(' · ');
  };
  const audienceOf = (item: LibraryFeedItem) =>
    item.mine && !item.shared
      ? { private: true, label: uploadCopy.private }
      : { private: false, label: item.audience ?? tags.shared };
  const chatAction = (file: CompanyFile) =>
    onUseInChat && readable(file) ? (
      <button
        type="button"
        className="pc-btn ws-library-item-action"
        data-variant="secondary"
        data-size="sm"
        onClick={() => onUseInChat(file.id)}
      >
        {uploadCopy.useInChat}
      </button>
    ) : null;

  const itemRow = (item: LibraryFeedItem) => (
    <LibraryItemRow
      key={item.id}
      id={item.id}
      kind={item.kind}
      name={item.title}
      picture={feedPicture(item)}
      meta={ownerLine(item)}
      audience={audienceOf(item)}
      time={timeLabel(item.updatedAt)}
      action={
        <DeleteLibraryItem
          kind={item.openTarget.kind === 'file' ? 'file' : 'result'}
          id={item.openTarget.id}
          title={item.title}
          canDelete={item.canDelete}
        />
      }
      onOpen={() => onOpen(item.openTarget)}
    />
  );
  const savedRow = (upload: LibraryUpload, file: CompanyFile) => {
    const item = feedFile.get(file.id);
    const stored =
      file.processingStatus === 'stored_only'
        ? file.failureCode === 'file_text_too_large'
          ? uploadCopy.storedOnlyTooLarge
          : uploadCopy.storedOnly
        : null;
    return (
      <LibraryItemRow
        key={upload.key}
        id={item?.id ?? file.id}
        kind="file"
        name={item?.title ?? file.name}
        meta={item ? ownerLine(item) : [viewerName, stored].filter(Boolean).join(' · ')}
        hint={!item && !filterTakesFiles && ready ? uploadCopy.notInFilter : null}
        audience={item ? audienceOf(item) : { private: true, label: uploadCopy.private }}
        time={timeLabel(item?.updatedAt ?? file.createdAt)}
        arrived={Date.now() - (upload.finishedAt ?? 0) < ARRIVAL_MS}
        action={
          <>
            {chatAction(file)}
            <DeleteLibraryItem
              kind="file"
              id={file.id}
              title={file.name}
              canDelete={item?.canDelete ?? file.canDelete}
            />
          </>
        }
        onOpen={() => onOpen({ kind: 'file', id: file.id })}
      />
    );
  };
  const uploadRow = (upload: LibraryUpload) => {
    const file = savedFile(upload);
    return file ? (
      savedRow(upload, file)
    ) : (
      <LibraryUploadRow
        key={upload.key}
        upload={upload}
        onRemove={() => libraryUploads.remove(upload.key)}
        onRetry={() => libraryUploads.retry(upload.key)}
      />
    );
  };

  const pinned = uploads.map(uploadRow);
  const sections: { group: LibraryDateGroup | null; rows: ReactNode[] }[] = searching
    ? [{ group: null, rows: [...pinned, ...listed.map(itemRow)] }]
    : groupByDate(listed, (item) => item.updatedAt).map(({ group, items }) => ({
        group,
        rows: items.map(itemRow),
      }));
  if (!searching && pinned.length) {
    const today = sections.find((section) => section.group === 'today');
    if (today) today.rows.unshift(...pinned);
    else sections.unshift({ group: 'today', rows: pinned });
  }

  const nothing = ready && feed.items.length === 0 && uploads.length === 0;
  // Empty Files is only an invitation to add files: the "Add files" row grows into it.
  const invite = nothing && !searching && feed.segment !== 'shared' && filesView;
  let emptyState: ReactNode = null;
  if (nothing && !invite) {
    if (searching)
      emptyState = (
        <EmptyState
          icon="results"
          title={panels.libraryNoMatchTitle}
          description={panels.libraryNoMatchBody}
        >
          <Button type="button" variant="outline" size="sm" onClick={() => feed.setQuery('')}>
            {copy.clearSearch}
          </Button>
        </EmptyState>
      );
    else if (feed.segment === 'shared')
      emptyState = (
        <EmptyState icon="results" title={copy.emptyShared} description={copy.emptySharedLine} />
      );
    else if (feed.group !== 'all')
      emptyState = (
        <EmptyState
          icon="results"
          title={copy[EMPTY_KIND[feed.group]]}
          description={copy.emptyKindLine}
        >
          <Button type="button" variant="outline" size="sm" onClick={onMake}>
            {panels.libraryMake}
          </Button>
        </EmptyState>
      );
    else
      emptyState = (
        <EmptyState
          icon="results"
          title={panels.libraryEmptyAllTitle}
          description={panels.libraryEmptyAllBody}
        >
          <Button type="button" variant="outline" size="sm" onClick={onMake}>
            {panels.libraryMake}
          </Button>
        </EmptyState>
      );
  }

  return (
    <section
      className="ws-library-feed"
      data-testid="library-feed"
      data-dragging={dragging ? true : undefined}
      ref={anchor}
    >
      <div className="ws-library-search">
        <WorkspaceIcon name="search" size={18} />
        <input
          type="text"
          role="searchbox"
          enterKeyHint="search"
          aria-label={copy.search}
          placeholder={copy.search}
          value={feed.query}
          onChange={(event) => feed.setQuery(event.currentTarget.value)}
        />
      </div>
      {feed.degraded && (
        <p className="ws-library-indexing" role="status">
          <WorkspaceIcon name="clock" size={14} />
          <span>{copy.indexing}</span>
          <button type="button" className="pc-link" onClick={feed.retry}>
            {copy.indexingRetry}
          </button>
        </p>
      )}
      <div className="ws-library-toolbar" ref={toolbar}>
        <Segmented<LibraryGroup>
          label={panels.libraryKindAll}
          value={feed.group}
          onChange={(group) => feed.group !== group && feed.setGroup(group)}
          options={(['all', 'documents', 'decks', 'sheets', 'files'] as const).map((group) => ({
            value: group,
            label: groupLabel[group],
            shortLabel: shortLabel[group],
          }))}
        />
        <LibraryOwnerMenu
          anchor={anchor}
          enabled={onScreen}
          value={feed.segment}
          onChange={feed.setSegment}
        />
      </div>
      {filesView && <LibraryAddRow ref={input} dragging={dragging} empty={invite} onFiles={add} />}
      <div className="ws-library-results">
        {feed.load === 'loading' && feed.items.length === 0 && (
          <LoadingState label={panels.libraryLoading} />
        )}
        {feed.load === 'error' && (
          <Callout
            tone="info"
            actions={
              <Button type="button" variant="outline" size="sm" onClick={feed.retry}>
                {panels.retry}
              </Button>
            }
          >
            {panels.libraryErrorBody}
          </Callout>
        )}
        {emptyState}
        {feed.load === 'loading' && feed.items.length > 0 && (
          <div
            className="ws-library-feed-progress"
            role="progressbar"
            aria-busy="true"
            aria-label={panels.libraryLoading}
          />
        )}
        {sections.map((section) => (
          <section key={section.group ?? 'results'} className="ws-library-group">
            {section.group && (
              <h2 className="ws-library-group-title">{copy[GROUP_TITLE[section.group]]}</h2>
            )}
            <ul className="ws-library-rows" aria-busy={feed.load === 'loading' || undefined}>
              {section.rows}
            </ul>
          </section>
        ))}
        {feed.hasMore && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ws-library-feed-more"
            onClick={feed.loadMore}
          >
            {panels.libraryMore}
          </Button>
        )}
        {searching && feed.appsMatchCount > 0 && (
          <button
            type="button"
            className="ws-library-apps-match"
            data-testid="library-apps-match"
            onClick={() => onOpenApps(feed.query)}
          >
            {formatCount(copy.appsMatchNoun, feed.appsMatchCount, locale)}
          </button>
        )}
      </div>
      <LibraryArchive />
      <p className="ws-sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
    </section>
  );
}
